// Avisos pelo WhatsApp — o que o SERVIDOR e o CLIENT precisam saber igual.
//
// Aviso é mensagem do SISTEMA, que sai do número do Rizoma (GZappy). Não é a
// mensagem da RELAÇÃO (romaneio, oferta, documento da carga), que continua saindo
// do WhatsApp da própria pessoa por wa.me — ver server/lib/mercado/mensagens.ts.
//
// ⚠️ O número hoje é o MESMO do AgroPlat, e as respostas caem no agente de lá.
// Por isso aviso nunca pergunta nada: leva um link e termina no rodapé fixo.
//
// O render e a validação moram AQUI, e não só no servidor, porque o editor da
// Plataforma (PR C) mostra a prévia e as verificações AO VIVO: duas cópias da
// regra de "linha com variável vazia some" é como a prévia passa a mentir.

// ─── Chaves ──────────────────────────────────────────────────────────────────

/**
 * Os modelos que existem — a lista é o contrato do prefixo `aviso.<chave>` de
 * `AgentCapacidade`. Modelo novo é CÓDIGO (precisa de gatilho): acrescentar aqui
 * e no catálogo (server/lib/avisos/catalogo.ts).
 *
 * ⚠️ `pagamento.confirmado` NÃO está aqui de propósito: desde o #246 ("quem
 * registra quita") o pagamento do comprador nasce confirmado e nenhum código
 * emite `PAGAMENTO_CONFIRMADO`. Um modelo sem gatilho seria uma linha morta na
 * tela da Plataforma. Volta quando o produtor voltar a confirmar.
 */
export const AVISO_CHAVES = [
  // Comprador
  "romaneio.recebido",
  "romaneio.corrigido",
  "registro.confirmado",
  "registro.divergencia",
  "pagamento.contestado",
  "vencimento.lembrete",
  "resumo.comprador",
  // Produtor
  "romaneio.divergencia",
  "pagamento.informado",
  "registro.recebido",
  "comprador.entrou",
  "resumo.produtor",
  // Os dois lados
  "vencimento.alterado",
] as const;

export type AvisoChave = typeof AVISO_CHAVES[number];

export function ehAvisoChave(valor: string): valor is AvisoChave {
  return (AVISO_CHAVES as readonly string[]).indexOf(valor) >= 0;
}

// ─── Categorias (o que cada pessoa liga e desliga em Meus avisos) ────────────

export const AVISO_CATEGORIAS = ["ROMANEIOS", "PAGAMENTOS", "LEMBRETES", "RESUMOS"] as const;
export type AvisoCategoria = typeof AVISO_CATEGORIAS[number];

export const CATEGORIA_AVISO: Record<AvisoCategoria, { rotulo: string; descricao: string }> = {
  ROMANEIOS: { rotulo: "Romaneios", descricao: "Romaneio recebido, corrigido, divergência, registro do comprador" },
  PAGAMENTOS: { rotulo: "Pagamentos", descricao: "Pagamento registrado, contestado, vencimento alterado" },
  LEMBRETES: { rotulo: "Lembretes", descricao: "Vencimentos do dia (6h30)" },
  RESUMOS: { rotulo: "Resumos", descricao: "Resumo do dia (comprador) ou da semana (produtor)" },
};

// ─── Entrega, público, situação ──────────────────────────────────────────────

/**
 * - IMEDIATO: sai no próximo tique do cron.
 * - AGRUPADO: espera a janela; 2+ do mesmo grupo (destinatário + modelo +
 *   contraparte, ou o lote) viram UMA mensagem com o texto de grupo.
 * - LEMBRETE / RESUMO: agendados, e só saem se houver conteúdo.
 */
export type AvisoEntrega = "IMEDIATO" | "AGRUPADO" | "LEMBRETE" | "RESUMO";

export const ENTREGA_AVISO_ROTULO: Record<AvisoEntrega, string> = {
  IMEDIATO: "Imediato",
  AGRUPADO: "Agrupado",
  LEMBRETE: "Lembrete agendado",
  RESUMO: "Resumo agendado",
};

/** Entregas que respeitam horário silencioso e limite diário. Lembrete e resumo têm hora própria. */
export function entregaDeEvento(entrega: AvisoEntrega): boolean {
  return entrega === "IMEDIATO" || entrega === "AGRUPADO";
}

export type AvisoPublico = "PRODUTOR" | "COMPRADOR" | "AMBOS";

export const AVISO_FILA_STATUS = [
  "PENDENTE", "ENVIADA", "AGRUPADA", "SEGURADA", "SUPRIMIDA", "CANCELADA", "FALHOU",
] as const;
export type AvisoFilaStatus = typeof AVISO_FILA_STATUS[number];

// ─── Regras globais (linha única de `aviso_regras`) ──────────────────────────

export interface RegrasAviso {
  /** Kill switch. Desligado, nada sai; a fila guarda e expira em 24 h. */
  envioLigado: boolean;
  /** "HH:MM" local. */
  silencioInicio: string;
  silencioFim: string;
  janelaMin: number;
  limiteDia: number;
  timezone: string;
}

export const REGRAS_AVISO_PADRAO: RegrasAviso = {
  envioLigado: false,
  silencioInicio: "21:00",
  silencioFim: "06:30",
  janelaMin: 10,
  limiteDia: 10,
  timezone: "America/Bahia",
};

/**
 * A FAIXA em que avisos saem (hora local): o cron de drenar só roda dentro dela
 * — fora, o Neon pode dormir. O horário silencioso só ESTREITA a faixa: começa
 * entre 20:00 e 22:00 e termina entre 06:00 e 08:00. Ver vercel.json e
 * server/routes/cron.ts.
 */
export const FAIXA_ENVIO_AVISO = { inicio: "06:00", fim: "22:00" } as const;
export const LIMITES_SILENCIO_AVISO = {
  inicio: { min: "20:00", max: "22:00" },
  fim: { min: "06:00", max: "08:00" },
} as const;

/** O início/fim do silêncio respeita a faixa? Devolve a frase do erro, ou null. */
export function erroNoSilencio(campo: "inicio" | "fim", hhmm: string): string | null {
  const lim = LIMITES_SILENCIO_AVISO[campo];
  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(hhmm)) return "Horário inválido (HH:MM)";
  if (hhmm < lim.min || hhmm > lim.max) {
    return campo === "inicio"
      ? `O silêncio começa entre ${lim.min} e ${lim.max} — os envios acontecem até ${FAIXA_ENVIO_AVISO.fim}.`
      : `O silêncio termina entre ${lim.min} e ${lim.max} — os envios começam às ${FAIXA_ENVIO_AVISO.inicio}.`;
  }
  return null;
}

export const JANELAS_AVISO_MIN = [5, 10, 30] as const;
export const LIMITES_AVISO_DIA = [6, 10, 20] as const;

/**
 * Quanto um aviso de evento pode esperar na fila (envio desligado, cron parado)
 * antes de virar SUPRIMIDA "expirou". Pausa curta não perde nada; pausa longa não
 * vira rajada de mensagem velha quando o envio volta.
 */
export const EXPIRACAO_AVISO_HORAS = 24;

// ─── Texto ───────────────────────────────────────────────────────────────────

/** Entra sozinho no fim de toda mensagem — o editor não deixa escrever. */
export const RODAPE_AVISO = "— Rizoma · aviso automático";
export const LIMITE_CARACTERES_AVISO = 1000;

export interface VariavelAviso {
  nome: string;
  obrigatoria: boolean;
}

/** `{romaneio.numero}` — minúsculas, dígitos, `_` e pontos entre segmentos. */
const PADRAO_VARIAVEL = /\{([a-z0-9_]+(?:\.[a-z0-9_]+)*)\}/g;

/** Par de surrogates = code point acima de U+FFFF = fora do BMP (vira ◆ no WhatsApp). */
const FORA_DO_BMP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
const FORA_DO_BMP_GLOBAL = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;

/** As variáveis citadas no texto, na ordem e sem repetição. */
export function variaveisDoTexto(texto: string): string[] {
  const vistas: string[] = [];
  const re = new RegExp(PADRAO_VARIAVEL.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto)) !== null) {
    if (vistas.indexOf(m[1]) < 0) vistas.push(m[1]);
  }
  return vistas;
}

function vazio(valor: string | null | undefined): boolean {
  return valor == null || String(valor).trim() === "";
}

/**
 * Monta a mensagem a partir do texto do modelo e das variáveis JÁ FORMATADAS.
 *
 * - `{var}` vira o valor; valor com várias linhas (a lista de um grupo) entra
 *   inteiro.
 * - Linha do TEXTO que cita variável ausente ou vazia sai INTEIRA — a mesma
 *   regra das mensagens do romaneio: nada de "por  em 24/09".
 * - Linhas em branco repetidas colapsam numa só; o rodapé fixo vai no fim.
 * - Caractere fora do BMP que venha dos DADOS (um nome com emoji) sai — o texto
 *   do modelo já foi barrado pelo editor.
 */
export function renderizarAviso(texto: string, vars: Record<string, string | null | undefined>): string {
  const linhas: string[] = [];
  for (const linha of texto.replace(/\r\n/g, "\n").split("\n")) {
    const citadas = variaveisDoTexto(linha);
    if (citadas.some(nome => vazio(vars[nome]))) continue;
    linhas.push(linha.replace(new RegExp(PADRAO_VARIAVEL.source, "g"), (_m, nome: string) =>
      String(vars[nome]).replace(FORA_DO_BMP_GLOBAL, "").trim()));
  }
  const corpo = linhas.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  return `${corpo}\n\n${RODAPE_AVISO}`;
}

/**
 * A amostra "Campo vazio" do editor: a primeira variável OPCIONAL que o texto usa
 * sai da amostra — a prévia mostra a linha dela sumindo. A mesma função no editor
 * e no "Enviar teste", para o teste mandar o que a prévia mostrou.
 */
export function amostraComCampoVazio(
  variaveis: VariavelAviso[],
  amostra: Record<string, string>,
  texto: string,
): { vars: Record<string, string>; vazia: string | null } {
  const vars: Record<string, string> = {};
  for (const k of Object.keys(amostra)) vars[k] = amostra[k];
  const opcional = variaveis.filter(v => !v.obrigatoria && texto.indexOf(`{${v.nome}}`) >= 0)[0];
  if (opcional) delete vars[opcional.nome];
  return { vars, vazia: opcional ? opcional.nome : null };
}

// ─── Verificações do editor ──────────────────────────────────────────────────

export type CodigoVerificacaoAviso =
  | "VAZIO"
  | "CHAVE_MALFORMADA"
  | "VARIAVEL_FORA_DO_CONTRATO"
  | "OBRIGATORIA_FALTANDO"
  | "FORA_DO_BMP"
  | "LINK_NAO_ISOLADO"
  | "LINK_COM_PONTUACAO"
  | "PERGUNTA"
  | "RODAPE_NO_TEXTO"
  | "LONGO_DEMAIS";

export interface VerificacaoAviso {
  codigo: CodigoVerificacaoAviso;
  ok: boolean;
  /** O que a tela mostra ao lado do ✓ / ✗. */
  rotulo: string;
}

/**
 * Todas as verificações de um texto contra o CONTRATO do modelo (as variáveis
 * que o código sabe preencher). A tela mostra a lista inteira, passando e
 * falhando; `validarTextoAviso` devolve só as que falharam, e é o que barra o
 * Publicar no servidor (422).
 */
export function verificarTextoAviso(texto: string, contrato: VariavelAviso[]): VerificacaoAviso[] {
  const t = texto.replace(/\r\n/g, "\n");
  const citadas = variaveisDoTexto(t);
  const nomes = contrato.map(v => v.nome);
  const fora = citadas.filter(n => nomes.indexOf(n) < 0);
  const faltando = contrato.filter(v => v.obrigatoria && citadas.indexOf(v.nome) < 0).map(v => v.nome);

  // Chave aberta ou fechada sem formar `{variavel}`: sobra de edição, ou
  // maiúscula/espaço dentro da chave. O render deixaria o `{` literal na mensagem.
  const semVariaveis = t.replace(new RegExp(PADRAO_VARIAVEL.source, "g"), "");
  const malformada = /[{}]/.test(semVariaveis);

  const linhas = t.split("\n");
  const naoVazias = linhas.map(l => l.trim()).filter(l => l !== "");
  const linhasComLink = linhas.filter(l => l.indexOf("{link}") >= 0);
  let linkIsolado = true;
  let linkSemPontuacao = true;
  if (linhasComLink.length > 0) {
    for (const l of linhasComLink) {
      const resto = l.replace("{link}", "").trim();
      if (resto === "") continue;
      // Só pontuação em volta (`{link}.`, `({link})`) é o erro que o WhatsApp
      // transforma em link quebrado; texto em volta é o link fora da linha própria.
      if (/^[.,;:!?)(\]["'»«…—-]+$/.test(resto)) linkSemPontuacao = false;
      else linkIsolado = false;
    }
    if (linhasComLink.length > 1 || naoVazias[naoVazias.length - 1] !== "{link}") {
      if (linkSemPontuacao) linkIsolado = false;
    }
  }

  return [
    { codigo: "VAZIO", ok: t.trim() !== "", rotulo: "O texto não está vazio" },
    { codigo: "CHAVE_MALFORMADA", ok: !malformada, rotulo: malformada ? "Há { ou } que não formam uma variável" : "Variáveis bem escritas, entre chaves" },
    {
      codigo: "VARIAVEL_FORA_DO_CONTRATO",
      ok: fora.length === 0,
      rotulo: fora.length === 0 ? "Todas as variáveis existem neste modelo" : `Variável fora deste modelo: ${fora.map(n => `{${n}}`).join(", ")}`,
    },
    {
      codigo: "OBRIGATORIA_FALTANDO",
      ok: faltando.length === 0,
      rotulo: faltando.length === 0
        ? `Obrigatórias presentes${contrato.some(v => v.obrigatoria) ? `: ${contrato.filter(v => v.obrigatoria).map(v => v.nome).join(", ")}` : ""}`
        : `Falta variável obrigatória: ${faltando.map(n => `{${n}}`).join(", ")}`,
    },
    {
      codigo: "LINK_NAO_ISOLADO",
      ok: linkIsolado,
      rotulo: linkIsolado ? "Link sozinho na última linha" : "O {link} precisa ficar sozinho, na última linha",
    },
    {
      codigo: "LINK_COM_PONTUACAO",
      ok: linkSemPontuacao,
      rotulo: linkSemPontuacao ? "Nada grudado no link" : "Pontuação grudada no {link} quebra o link no WhatsApp",
    },
    { codigo: "FORA_DO_BMP", ok: !FORA_DO_BMP.test(t), rotulo: FORA_DO_BMP.test(t) ? "Há emoji ou símbolo fora do BMP (vira ◆ no WhatsApp)" : "Sem caracteres fora do BMP" },
    { codigo: "PERGUNTA", ok: t.indexOf("?") < 0, rotulo: t.indexOf("?") < 0 ? "Sem pergunta ao leitor (ninguém lê a resposta)" : "Tem “?”: ninguém lê a resposta deste número" },
    {
      codigo: "RODAPE_NO_TEXTO",
      ok: t.indexOf("aviso automático") < 0,
      rotulo: t.indexOf("aviso automático") < 0 ? "Sem o rodapé no texto (ele entra sozinho)" : "Tire o rodapé: ele entra sozinho no fim",
    },
    {
      codigo: "LONGO_DEMAIS",
      ok: t.length <= LIMITE_CARACTERES_AVISO,
      rotulo: `${t.length} de ${LIMITE_CARACTERES_AVISO} caracteres`,
    },
  ];
}

export function validarTextoAviso(texto: string, contrato: VariavelAviso[]): VerificacaoAviso[] {
  return verificarTextoAviso(texto, contrato).filter(v => !v.ok);
}

// ─── Telefone mascarado (Envios, Meus avisos) ────────────────────────────────

/** "74999312345" → "(74) 9 9931-····". Só dígitos entram; o resto é ignorado. */
export function mascararTelefoneAviso(telefone: string | null | undefined): string {
  let d = String(telefone ?? "").replace(/\D/g, "");
  if (d.length > 11 && d.indexOf("55") === 0) d = d.substring(2);
  if (d.length < 10) return d ? "····" : "";
  const ddd = d.substring(0, 2);
  const numero = d.substring(2);
  if (numero.length === 9) return `(${ddd}) ${numero.charAt(0)} ${numero.substring(1, 5)}-····`;
  return `(${ddd}) ${numero.substring(0, 4)}-····`;
}
